import { t } from "@lingui/macro";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useFragment } from "react-relay";

import { MyScheduleCard } from "@whoosh/design-system/src/apps/PatronApp/components/MyScheduleCard/MyScheduleCard";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { dateUtils } from "@whoosh/utilities";

import { EventSignUpCardWrapper_EventSignUp$key } from "./__generated__/EventSignUpCardWrapper_EventSignUp.graphql";

const formattedDate = (startOn: string) => {
  const dateObj = startOn ? parse(startOn, "yyyy-MM-dd", new Date()) : null;

  const dateFormatted = dateObj ? `${format(dateObj, "EEEE, MMM do")}` : "";

  return dateFormatted;
};

type EventSignUpCardWrapperProps = {
  eventSignUpCardWrapperRef: EventSignUpCardWrapper_EventSignUp$key;
};

export const EventSignUpCardWrapper = ({
  eventSignUpCardWrapperRef,
}: EventSignUpCardWrapperProps) => {
  const eventSignUp = useFragment(
    EventSignUpCardWrapperFragmentString,
    eventSignUpCardWrapperRef
  );

  const linkProps = useLinkProps({
    screen: "EventDetail",
    params: {
      eventID: eventSignUp?.event?.id,
    },
  });

  const isToday = dateUtils.useIsToday(eventSignUp?.event?.startOn || "");

  const formattedEventSignUp = {
    id: eventSignUp?.id || "",
    title: t`Event Registration`,
    subTitle: eventSignUp?.event?.eventSeries.name || "",
    date: isToday ? "Today" : formattedDate(eventSignUp?.event?.startOn || ""),
    timeRange: `${dateUtils.formatHHMM(
      eventSignUp?.event?.eventSessions?.edges?.[0]?.node?.startAt || ""
    )} - ${dateUtils.formatHHMM(
      eventSignUp?.event?.eventSessions?.edges?.[0]?.node?.endAt || ""
    )}`,
    cardColor: eventSignUp?.event?.eventSeries?.color || "A",
    hasPairings: (eventSignUp?.bookings?.count || 0) > 0,
    departmentType:
      eventSignUp?.event?.eventSeries?.facility?.department?.type || "GOLF",
    departmentName:
      eventSignUp?.event?.eventSeries?.facility?.department?.name || "Golf",
    // rework this in main component to pass just string into t
    courseNames: () => {
      const courses =
        eventSignUp?.event?.eventSessions?.edges?.[0]?.node?.courses?.edges?.flatMap(
          (e) => {
            return e?.node?.id
              ? [
                  {
                    id: e.node.id,
                    name: e.node.name,
                    courseBucketName: e.node.courseBucket?.name,
                  },
                ]
              : [];
          }
        ) ?? [];

      let coursesString = "";

      if (courses.length > 1) {
        coursesString += `${courses.length} ${
          eventSignUp?.event?.eventSeries?.facility.copyOverrides?.course
            ?.plural || "Courses"
        }`;
      } else {
        coursesString += courses.map((c, i) =>
          c.courseBucketName
            ? `${c.courseBucketName}: ${c.name}${
                i < courses.length - 1 ? ", " : ""
              }`
            : `${c.name}${i < courses.length - 1 ? ", " : ""}`
        );
      }

      return coursesString;
    },
    onPress: () => linkProps.onPress(),
    signupStatus: eventSignUp?.event?.signUpStatusForClient || "CONFIRMED",
  };

  return (
    <Box width={305}>
      <MyScheduleCard
        {...formattedEventSignUp}
        courseNames={
          formattedEventSignUp ? formattedEventSignUp.courseNames() : undefined
        }
      />
    </Box>
  );
};

export const EventSignUpCardWrapperFragmentString = graphql`
  fragment EventSignUpCardWrapper_EventSignUp on EventSignUp {
    id
    bookings {
      count
    }
    event {
      id
      startOn
      tags(first: 1000) {
        edges {
          node {
            name
          }
        }
      }
      signUpStatusForClient(clientId: $memberId)
      eventSeries {
        name
        color
        facility {
          name
          copyOverrides {
            course {
              plural
            }
          }
          department {
            type
            name
          }
        }
      }
      eventSessions {
        edges {
          node {
            startAt
            endAt
            courses {
              edges {
                node {
                  id
                  name
                  courseBucket {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
